import type { PendingReport } from '../../types';
import type { ExportPayload, ExportEmployeeRow, ExportReportRow, ExportAllowanceRow, ExportSupervisionBonusRow } from './exportTypes';
import type { ExportColumnId, ExportGroupBy } from './exportColumns';
import type { TimeRange } from '../timeRange';
import { formatTimeRangeLabel } from '../timeRange';
import { localizedName } from '@/utils/localized';

interface EmployeeGroup {
  employeeId: string;
  employeeName: string;
  reports: PendingReport[];
  totalReports: number;
  totalQuantity: number;
  totalAmount: number;
}

interface AllowanceData {
  id: string;
  name: string;
  nameEn: string | null;
  code: string;
  amount: number;
  period: string;
}

interface SupervisionBonusBreakdown {
  hospitalId: string;
  hospitalName: string;
  hospitalNameEn: string | null;
  hospitalCode: string;
  reportTypeCode: string;
  totalUnits: number;
  bonusPerUnit: number;
  amount: number;
}

interface NormalizeParams {
  title: string;
  groups: EmployeeGroup[];
  allowancesByEmployee: Record<string, AllowanceData[]>;
  supervisionBonusesByEmployee?: Record<string, { totalAmount: number; breakdown: SupervisionBonusBreakdown[] }>;
  selectedIds: Set<string>;
  selectionMode: 'employee' | 'report';
  currencySymbol: string;
  language: string;
  timeRange: TimeRange;
  filters?: {
    status?: string;
    hospitalName?: string;
    roleName?: string;
  };
  columns?: ExportColumnId[];
  groupBy?: ExportGroupBy;
}

export function normalizeExportData(params: NormalizeParams): ExportPayload {
  const {
    title,
    groups,
    allowancesByEmployee,
    supervisionBonusesByEmployee = {},
    selectedIds,
    selectionMode,
    currencySymbol,
    language,
    timeRange,
    filters = {},
    columns,
    groupBy = 'employee',
  } = params;

  const isRTL = language === 'he';
  const locale = language === 'he' ? 'he-IL' : 'en-US';

  // Format month for display
  const formattedMonth = formatTimeRangeLabel(timeRange, locale);

  // Filter groups based on selection
  const filteredGroups = groups.filter((g) => {
    if (selectedIds.size === 0) return true;
    if (selectionMode === 'employee') return selectedIds.has(g.employeeId);
    return g.reports.some((r) => selectedIds.has(r.id));
  });

  // Build flat report rows from all groups
  const allReportRows: (ExportReportRow & { _groupKey: string; _employeeId: string })[] = [];

  for (const g of filteredGroups) {
    const filteredReports = selectionMode === 'report' && selectedIds.size > 0
      ? g.reports.filter((r) => selectedIds.has(r.id))
      : g.reports;

    const roleName = filteredReports[0]?.therapist?.employmentRoleName;
    const roleNameEn = filteredReports[0]?.therapist?.employmentRoleNameEn;
    const empRole = language === 'he'
      ? (roleName ?? '')
      : (roleNameEn || roleName || '');

    for (const r of filteredReports) {
      const reportTypeName = r.reportTypeObj
        ? localizedName(r.reportTypeObj, language)
        : r.reportTypeCode || r.reportType;
      const hospitalName = r.hospital ? localizedName(r.hospital, language) : '';

      let _groupKey: string;
      switch (groupBy) {
        case 'hospital':
          _groupKey = hospitalName || 'Unknown';
          break;
        case 'reportType':
          _groupKey = reportTypeName;
          break;
        case 'flat':
          _groupKey = '__flat__';
          break;
        default: // 'employee'
          _groupKey = g.employeeName;
      }

      allReportRows.push({
        date: r.date,
        status: r.status,
        reportType: reportTypeName,
        hospital: hospitalName,
        employee: g.employeeName,
        employmentRole: empRole,
        quantity: r.quantity || 1,
        ratePerUnit: r.ratePerUnit ?? 0,
        hospitalBonus: r.hospitalBonusPerUnit ?? 0,
        amount: r.calculatedAmount ?? 0,
        notes: r.description ?? '',
        _groupKey,
        _employeeId: g.employeeId,
      });
    }
  }

  // Group rows
  const groupMap = new Map<string, typeof allReportRows>();
  for (const r of allReportRows) {
    const key = r._groupKey;
    if (!groupMap.has(key)) groupMap.set(key, []);
    groupMap.get(key)!.push(r);
  }

  // Build ExportEmployeeRow per group
  const rows: ExportEmployeeRow[] = [...groupMap.entries()].map(([groupName, reports]) => {
    // Collect unique employee IDs in this group for allowances
    const employeeIds = new Set(reports.map(r => r._employeeId));

    // When groupBy=employee (default), include allowances for the single employee
    // For other groupBy modes, sum allowances for all employees in the group
    let allowanceRows: ExportAllowanceRow[] = [];
    let allowancesAmount = 0;
    let supervisionBonusRows: ExportSupervisionBonusRow[] = [];
    let supervisionBonusAmount = 0;
    if (groupBy === 'employee' && employeeIds.size === 1) {
      const tid = [...employeeIds][0];
      const allowances = allowancesByEmployee[tid] || [];
      allowancesAmount = allowances.reduce((sum, a) => sum + a.amount, 0);
      allowanceRows = allowances.map((a) => ({
        name: language === 'he' ? a.name : (a.nameEn || a.name),
        amount: a.amount,
        period: a.period,
      }));

      const svBonus = supervisionBonusesByEmployee[tid];
      if (svBonus) {
        supervisionBonusAmount = svBonus.totalAmount;
        supervisionBonusRows = svBonus.breakdown.map((b) => ({
          hospitalName: language === 'he' ? b.hospitalName : (b.hospitalNameEn || b.hospitalName),
          reportTypeCode: b.reportTypeCode,
          totalUnits: b.totalUnits,
          bonusPerUnit: b.bonusPerUnit,
          amount: b.amount,
        }));
      }
    }

    const reportsAmount = reports
      .filter((r) => r.status !== 'REJECTED')
      .reduce((sum, r) => sum + r.amount, 0);

    const displayName = groupName === '__flat__' ? title : groupName;

    // For the group header role label
    let groupRole = '';
    if (groupBy === 'employee' && reports.length > 0) {
      groupRole = reports[0].employmentRole;
    }

    return {
      employeeName: displayName,
      employmentRole: groupRole,
      totalReports: reports.filter((r) => r.status !== 'REJECTED').length,
      totalQuantity: reports
        .filter((r) => r.status !== 'REJECTED')
        .reduce((sum, r) => sum + r.quantity, 0),
      reportsAmount,
      allowancesAmount,
      supervisionBonusAmount,
      totalAmount: reportsAmount + allowancesAmount + supervisionBonusAmount,
      reports: reports.map(({ _groupKey, _employeeId, ...rest }) => rest),
      allowances: allowanceRows,
      supervisionBonuses: supervisionBonusRows,
    };
  });

  const totals = {
    employeeCount: rows.length,
    reportCount: rows.reduce((sum, r) => sum + r.totalReports, 0),
    quantity: rows.reduce((sum, r) => sum + r.totalQuantity, 0),
    totalAmount: rows.reduce((sum, r) => sum + r.totalAmount, 0),
  };

  return {
    title,
    month: formattedMonth,
    generatedAt: new Date().toLocaleString(locale),
    currencySymbol,
    isRTL,
    language,
    filters,
    columns,
    groupBy,
    rows,
    totals,
  };
}
